package com.elan.edubridge.ui.staff

import com.elan.edubridge.data.firebase.AcademySubscriptionReader
import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.Router
import com.elan.edubridge.ui.shell.accountName
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.renderPage
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlin.js.Date
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement

/**
 * 스태프 콘솔 공통 셸(가이드 §3.0 DashboardShell). 딥그린 사이드바 7항목(§5.3) + topbar.
 *
 * S5의 `AppHub` placeholder에 있던 셸 마크업을 이 파일로 옮기고 실 상호작용(학원 전환, 로그아웃)을
 * 붙였다 — `AppHub`는 이제 `/app` 리다이렉트 판정과 각 스태프 페이지로의 라우팅 분기만 한다.
 *
 * 모든 스태프 페이지(`TodayDashboardPage`, `StudentsPage`, `QrKioskPage` 관리 화면, ...)는
 * [render]를 호출해 콘텐츠 HTML만 넘긴다 — 사이드바·topbar를 각 페이지가 중복 구현하지 않는다.
 */
object DashboardShell {
    private val scope = MainScope()
    private val accountRepository = SharedGraph.createAccountRepository()
    private var renderGeneration = 0

    private const val SUBSCRIPTION_BANNER_SLOT_ID = "subscription-banner-slot"

    private class SidebarItem(
        val section: String,
        val label: String,
        val iconId: String,
        val minimumPermission: Permission = Permission.VIEWER,
        val path: (String) -> String,
    )

    /** §5.3 순서 그대로: 오늘 수업 / 학생·출결 QR / 공지·과제 / 통계·설정. 가이드 §7의 4그룹. */
    private val sidebarGroups: List<Pair<String, List<SidebarItem>>> = listOf(
        "" to listOf(SidebarItem("today", "오늘 수업", "icon-home") { id -> "/app/$id/today" }),
        "현장 운영" to listOf(
            SidebarItem("students", "학생", "icon-students") { id -> "/app/$id/students" },
            SidebarItem("attendance-qr", "출결 QR", "icon-qr") { id -> "/app/$id/attendance-qr" },
        ),
        "소통" to listOf(
            SidebarItem("notices", "공지", "icon-notice") { id -> "/app/$id/notices" },
            SidebarItem("board", "게시판", "icon-notice") { id -> "/app/$id/board" },
            SidebarItem("assignments", "과제", "icon-assignment") { id -> "/app/$id/assignments" },
        ),
        "운영 관리" to listOf(
            SidebarItem("stats", "통계", "icon-stats") { id -> "/app/$id/stats" },
            SidebarItem("reports", "신고 관리", "icon-warning", Permission.EDITOR) { id -> "/app/$id/reports" },
            // W7 참여 코드로 들어온 학생 가입 신청 큐. 승인 권한이 EDITOR라 신고 관리와 같은 급이다.
            SidebarItem("join-applications", "가입 신청", "icon-students", Permission.EDITOR) { id -> "/app/$id/join-applications" },
            // ADMIN 전용(가이드 §7 근거 5). TEACHER(EDITOR)에게는 이 항목 자체를 렌더하지 않는다.
            SidebarItem("settings", "설정", "icon-settings", Permission.ADMIN) { id -> "/app/$id/settings" },
        ),
    )

    /**
     * @param contentHtml `<main id="content">` 안에 그대로 삽입된다 — 페이지가 이미 완전한 조각을 만들어 온다.
     * @param afterRender 콘텐츠가 DOM에 붙은 뒤 호출된다. 페이지 자신의 리스너(버튼 클릭 등)는 여기서 붙인다 —
     *   [render] 자신은 셸(사이드바·topbar) 리스너만 안다.
     */
    fun render(
        session: SessionState,
        academyId: String,
        activeSection: String,
        contentHtml: String,
        afterRender: () -> Unit = {},
    ) {
        val generation = ++renderGeneration
        renderPage("eb-shell", chromeHtml(session, academyId, activeSection, contentHtml))
        wireChrome(session, academyId, activeSection)
        wireSubscriptionBanner(academyId, generation)
        afterRender()
    }

    private fun chromeHtml(session: SessionState, academyId: String, activeSection: String, contentHtml: String): String {
        val academyName = session.usableMemberships.firstOrNull { it.academyId == academyId }?.academyName
            ?: session.activeMembership?.academyName ?: "내 학원"
        val nav = sidebarGroups.joinToString("") { (groupLabel, items) ->
            val visibleItems = items.filter { session.activePermission.atLeast(it.minimumPermission) }
            if (visibleItems.isEmpty()) return@joinToString ""
            val groupAttrs = if (groupLabel.isEmpty()) "" else """ role="group" aria-label="${groupLabel.escapeHtml()}""""
            val links = visibleItems.joinToString("") { item ->
                val active = item.section == activeSection
                val activeClass = if (active) " eb-sidebar__item--active" else ""
                val current = if (active) """ aria-current="page"""" else ""
                """<a class="eb-sidebar__item$activeClass" href="${item.path(academyId)}" data-section="${item.section}"$current>${icon(item.iconId, "md")}<span>${item.label}</span></a>"""
            }
            """<div class="eb-sidebar__group"$groupAttrs>$links</div>"""
        }

        val canSwitchAcademy = session.usableMemberships.size > 1
        val academySwitchAttrs = if (canSwitchAcademy) {
            """ id="academy-switch-trigger" aria-haspopup="listbox" aria-expanded="false""""
        } else {
            " disabled"
        }
        val academyOptions = if (!canSwitchAcademy) "" else """
            <div class="eb-popover-menu" id="academy-switch-menu" role="listbox" aria-label="학원 선택" hidden>
                ${
            session.usableMemberships.joinToString("") { membership ->
                val selected = if (membership.academyId == academyId) """ aria-selected="true"""" else ""
                """<button role="option" data-academy-id="${membership.academyId}"$selected>${membership.academyName.escapeHtml()}</button>"""
            }
        }
            </div>
        """.trimIndent()

        return """
            <aside class="eb-sidebar" aria-label="주 메뉴" id="dashboard-sidebar">
                <div class="eb-sidebar__brand">${icon("icon-bridge-logo", "lg")}<span class="eb-sidebar__brand-name">EduBridge</span></div>
                <nav class="eb-sidebar__nav">$nav</nav>
                <a class="eb-sidebar__help" href="/guide">${icon("icon-help", "sm")}<span>이용 가이드</span></a>
                <!-- session.availableRoles가 1개뿐이면 정적 라벨(가이드 §3.0) — 역할 전환 인터랙션은 범위 밖, 학원 전환은 topbar가 담당 -->
                <button class="eb-sidebar__role" disabled>
                    <span class="eb-avatar eb-avatar--sm">${avatarInitial(session)}</span>
                    <span>${session.activeRole.displayName}</span>
                </button>
            </aside>
            <!-- <1024px에서만 보인다(§5) — 사이드바가 display:none 대신 오프캔버스 드로어로 바뀌는 동안 배경을 덮는다 -->
            <div class="eb-sidebar-backdrop" id="sidebar-backdrop" hidden></div>
            <div class="eb-shell__main">
                <header class="eb-topbar">
                    <button class="eb-icon-button eb-topbar__menu-trigger" id="sidebar-menu-trigger" aria-label="메뉴 열기" aria-controls="dashboard-sidebar" aria-expanded="false">
                        ${icon("icon-menu", "md")}
                    </button>
                    <div style="position: relative;">
                        <button class="eb-academy-switch"$academySwitchAttrs>
                            <span>${academyName.escapeHtml()}</span>${icon("icon-chevron-down", "xs")}
                        </button>
                        $academyOptions
                    </div>
                    <div class="eb-topbar__actions">
                        <button class="eb-icon-button" aria-label="알림" disabled>${icon("icon-notification", "md")}<span class="eb-badge-dot" aria-hidden="true"></span></button>
                        <button class="eb-account-menu" id="logout-button" aria-label="로그아웃">
                            ${icon("icon-logout", "sm")}<span>로그아웃</span>
                        </button>
                    </div>
                </header>
                <div id="$SUBSCRIPTION_BANNER_SLOT_ID" data-academy-id="${academyId.escapeHtml()}"></div>
                <main id="content" class="eb-shell__content" tabindex="-1">$contentHtml</main>
            </div>
        """.trimIndent()
    }

    private fun wireChrome(session: SessionState, academyId: String, activeSection: String) {
        (document.getElementById("logout-button") as? HTMLButtonElement)?.onclick = {
            // 로그인 화면 복귀는 세션 구독(Main.kt)이 처리한다.
            scope.launch { accountRepository.logout() }
            null
        }

        wireMobileSidebar()

        val trigger = document.getElementById("academy-switch-trigger") as? HTMLButtonElement ?: return
        val menu = document.getElementById("academy-switch-menu") as? HTMLElement ?: return
        trigger.onclick = {
            val nowHidden = menu.hasAttribute("hidden")
            if (nowHidden) menu.removeAttribute("hidden") else menu.setAttribute("hidden", "")
            trigger.setAttribute("aria-expanded", nowHidden.toString())
            null
        }
        val options = menu.querySelectorAll("button[data-academy-id]")
        for (index in 0 until options.length) {
            val option = options.item(index) as? HTMLButtonElement ?: continue
            option.onclick = {
                val targetId = option.getAttribute("data-academy-id")
                if (!targetId.isNullOrEmpty() && targetId != academyId) {
                    accountRepository.selectAcademy(targetId)
                    Router.navigate("/app/$targetId/$activeSection")
                } else {
                    menu.setAttribute("hidden", "")
                    trigger.setAttribute("aria-expanded", "false")
                }
                null
            }
        }
    }

    private fun wireSubscriptionBanner(academyId: String, generation: Int) {
        scope.launch {
            val subscription = try {
                AcademySubscriptionReader.read(academyId)
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                return@launch
            } ?: return@launch
            val content = subscriptionBannerContent(
                state = subscription.state,
                expiryTimeMillis = subscription.expiryTimeMillis,
                nowMillis = Date().getTime(),
            ) ?: return@launch
            if (generation != renderGeneration) return@launch
            val slot = document.getElementById(SUBSCRIPTION_BANNER_SLOT_ID) as? HTMLElement ?: return@launch
            if (slot.getAttribute("data-academy-id") != academyId) return@launch
            slot.innerHTML = subscriptionBannerHtml(content)
        }
    }

    /**
     * <1024px에서 사이드바 숨김의 대체 내비게이션(가이드 §5 `.eb-topbar__menu-trigger`, 계획 후속 재점검).
     * `.eb-sidebar`는 CSS가 `display:none` 대신 오프캔버스로 밀어두므로, 여기서는 `--open` 클래스와
     * 백드롭 표시만 토글한다 — 1024px 이상에서는 트리거 자체가 CSS로 숨겨져 클릭될 일이 없다.
     */
    private fun wireMobileSidebar() {
        val trigger = document.getElementById("sidebar-menu-trigger") as? HTMLButtonElement ?: return
        val sidebar = document.getElementById("dashboard-sidebar") as? HTMLElement ?: return
        val backdrop = document.getElementById("sidebar-backdrop") as? HTMLElement ?: return

        fun close() {
            sidebar.classList.remove("eb-sidebar--open")
            backdrop.setAttribute("hidden", "")
            trigger.setAttribute("aria-expanded", "false")
        }

        trigger.onclick = {
            val opening = !sidebar.classList.contains("eb-sidebar--open")
            if (opening) {
                sidebar.classList.add("eb-sidebar--open")
                backdrop.removeAttribute("hidden")
                trigger.setAttribute("aria-expanded", "true")
            } else {
                close()
            }
            null
        }
        backdrop.onclick = { close(); null }
    }

    private fun avatarInitial(session: SessionState): String {
        val name = session.accountName()
        return if (name.isEmpty()) "?" else name.take(1)
    }
}
